import type {
  FrontendApi,
  RecoveryFlow,
  UiNodeInputAttributes,
  UpdateRecoveryFlowBody,
} from '@ory/kratos-client';
import type { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useLoaderData, useNavigate, useSearchParams } from 'react-router-dom';

import { oryClient } from '@revelio/auth';
import { validateEmail } from '@revelio/iso-utility';

import { carriedAuthParams } from './auth-params';
import { ThemeHeading, ThemeLink, ThemeTagline } from './theme/theme';
import {
  Button,
  Field,
  LinkButton,
  Spinner,
  Stack,
  TextInput,
  uiStyles,
} from './ui';
import { CodeInput } from './ui/code-input';

// Kratos recovery codes are 6-digit, same as the signup verification codes.
const RECOVERY_CODE_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;

const flowCsrfToken = (flow: RecoveryFlow | undefined) => {
  const node = flow?.ui.nodes.find(
    (n) => (n.attributes as UiNodeInputAttributes).name === 'csrf_token'
  );
  return (node?.attributes as UiNodeInputAttributes | undefined)?.value;
};

// Kratos reports invalid/expired codes as messages on the flow it returns —
// on the flow itself or on the offending node, and sometimes both, so dedupe
// by id rather than rendering the same sentence twice.
const flowErrors = (flow: RecoveryFlow | undefined) => {
  const errors = [
    ...(flow?.ui.messages ?? []),
    ...(flow?.ui.nodes ?? []).flatMap((n) => n.messages),
  ].filter((message) => message.type === 'error');
  return [...new Map(errors.map((message) => [message.id, message])).values()];
};

export function RecoveryForm() {
  const loaderData = useLoaderData() as {
    validAuth: boolean;
    flow: RecoveryFlow;
  };
  const [flow, setFlow] = useState<RecoveryFlow | undefined>(loaderData.flow);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Failures Kratos doesn't describe in the flow itself (network, 5xx).
  const [submitError, setSubmitError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (resendCooldown <= 0) {
      return;
    }
    const timer = setInterval(
      () => setResendCooldown((seconds) => seconds - 1),
      1000
    );
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const carried = carriedAuthParams(searchParams).toString();
  const loginHref = carried ? `/login?${carried}` : '/login';
  const settingsHref = carried ? `/settings?${carried}` : '/settings';

  const isCodeStep = flow?.state === 'sent_email';
  const errors = flowErrors(flow);

  const submitFlow = async (body: UpdateRecoveryFlowBody): Promise<boolean> => {
    // something unexpected went wrong and the flow was not set
    if (!flow) {
      navigate('/login', { replace: true });
      return false;
    }

    setSubmitting(true);
    setSubmitError('');
    try {
      const { data } = await (oryClient as FrontendApi).updateRecoveryFlow({
        flow: flow.id,
        updateRecoveryFlowBody: body,
      });
      setFlow(data);

      // A verified code upgrades the session and the flow leaves the code
      // step — continue to the change-password screen.
      if (data.active !== 'code') {
        navigate(settingsHref, { replace: true });
      }
      return true;
    } catch (e) {
      const status = (e as AxiosError).response?.status;

      // TODO: when a new version of Ory fixes their issue with returning a
      // 422 then we can remove this
      if (status === 422 || String(e).includes('422')) {
        navigate(settingsHref, { replace: true });
        return false;
      }

      // A rejected code (wrong, expired, already used) comes back as a 400
      // carrying the flow, with the reason in its messages — the SDK throws
      // it rather than returning it, so re-seat the flow here or the user
      // gets a dead button and no explanation.
      const rejectedFlow = (e as AxiosError<RecoveryFlow>).response?.data;
      if (status === 400 && rejectedFlow?.ui) {
        setFlow(rejectedFlow);
        return false;
      }

      // An expired flow is 410 Gone and Kratos never resurrects it, so
      // retrying against the same id can only 410 again. Re-seat a fresh
      // flow (back at the email step, email preserved) and say why; the
      // URL's ?flow= is updated so a reload doesn't reload the dead id.
      if (status === 410) {
        try {
          const { data: freshFlow } = await (
            oryClient as FrontendApi
          ).createBrowserRecoveryFlow();
          setFlow(freshFlow);
          setCode('');
          const params = new URLSearchParams(searchParams);
          params.set('flow', freshFlow.id);
          setSearchParams(params, { replace: true });
          setSubmitError(
            "That code expired — re-enter your email and we'll send a fresh one."
          );
        } catch (createError) {
          console.error(createError);
          setSubmitError('Something went wrong, please try again.');
        }
        return false;
      }

      console.error(e);
      setSubmitError('Something went wrong, please try again.');
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  // Only start the cooldown once Kratos has actually accepted the request —
  // otherwise a failed send locks the user out of retrying for 30s.
  const sendCode = async () => {
    const sent = await submitFlow({
      method: 'code',
      email,
      csrf_token: flowCsrfToken(flow),
    });
    if (sent) {
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    }
  };

  const handleSendCode = async () => {
    if (!validateEmail(email)) {
      setEmailError('Valid email is required');
      return;
    }
    setEmailError('');
    await sendCode();
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0 || !email) {
      return;
    }
    setCode('');
    await sendCode();
  };

  // CodeInput's value is positional — a cleared box in the middle leaves a
  // space behind — so a part-entered code can still be six characters wide.
  // Kratos would reject it and burn one of the flow's limited attempts.
  const handleSubmitCode = (value: string) => {
    if (value.replace(/\s/g, '').length !== RECOVERY_CODE_LENGTH) {
      return Promise.resolve(false);
    }
    return submitFlow({
      method: 'code',
      code: value,
      csrf_token: flowCsrfToken(flow),
    });
  };

  if (isCodeStep) {
    return (
      <Stack gap="var(--ap-code-gap)" fullWidth>
        <Stack gap="12px">
          <ThemeHeading>Check your email</ThemeHeading>
          <ThemeTagline>
            We've sent a code to {email || 'your email'},
            <br />
            please enter it below to reset your password
          </ThemeTagline>
        </Stack>

        <form
          onSubmit={(event) => {
            event.preventDefault();
            handleSubmitCode(code);
          }}
        >
          <CodeInput
            length={RECOVERY_CODE_LENGTH}
            value={code}
            disabled={submitting}
            onChange={setCode}
            onComplete={handleSubmitCode}
            ariaLabel={(position, length) =>
              `Recovery code digit ${position} of ${length}`
            }
          />
          {submitting && (
            <span className={`${uiStyles.buttonLoading} ${uiStyles.verifying}`}>
              <ThemeTagline>Verifying...</ThemeTagline>
              <Spinner size="xs" className={uiStyles.mutedSpinner} />
            </span>
          )}
          {errors.map((message) => (
            <p key={message.id} className={uiStyles.formError}>
              {message.text}
            </p>
          ))}
          {submitError && <p className={uiStyles.formError}>{submitError}</p>}
        </form>

        {email && (
          <ThemeTagline>
            Didn't receive your code?{' '}
            <LinkButton
              aria-disabled={resendCooldown > 0 || submitting}
              style={{
                pointerEvents:
                  resendCooldown > 0 || submitting ? 'none' : 'auto',
                opacity: resendCooldown > 0 ? 0.6 : undefined,
              }}
              onClick={handleResendCode}
            >
              Resend code{resendCooldown > 0 ? ` ${resendCooldown}s` : ''}
            </LinkButton>
          </ThemeTagline>
        )}
      </Stack>
    );
  }

  return (
    <Stack gap="var(--ap-cta-gap)" fullWidth>
      <Stack gap="12px">
        <ThemeHeading>Reset your password</ThemeHeading>
        <ThemeTagline>
          Enter the email for your account and we'll send you a code to reset
          your password
        </ThemeTagline>
      </Stack>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          handleSendCode();
        }}
      >
        <Stack gap="16px">
          <Field error={emailError}>
            <TextInput
              type="email"
              name="email"
              placeholder="Email"
              aria-label="Email"
              autoComplete="email"
              invalid={!!emailError}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>
          <Button
            style={{ marginTop: '8px' }}
            disabled={submitting}
            type="submit"
          >
            {submitting ? (
              <span className={uiStyles.buttonLoading}>
                Sending code...
                <Spinner size="xs" />
              </span>
            ) : (
              'Continue'
            )}
          </Button>
        </Stack>
        {errors.map((message) => (
          <p key={message.id} className={uiStyles.formError}>
            {message.text}
          </p>
        ))}
        {submitError && <p className={uiStyles.formError}>{submitError}</p>}
      </form>

      <ThemeTagline>
        Remembered your password? <ThemeLink href={loginHref}>Log in</ThemeLink>
      </ThemeTagline>
    </Stack>
  );
}
